import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useAnalytics } from "../../hooks/useAnalytics";
import { takeNavMark } from "../../lib/navPerf";
import Sidebar from "../Dashboard/Sidebar/Sidebar";
import MobileTopBar from "../Dashboard/MobileTopBar/MobileTopBar";
import { MOBILE_TOPBAR_HEIGHT } from "../../contexts/SidebarContext";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { TopBarSlotProvider } from "../../contexts/TopBarSlotContext";
import BottomTabBar, { BOTTOM_TAB_BAR_HEIGHT, BOTTOM_TAB_BAR_SAFE_AREA } from "./BottomTabBar";
import { useEdgeSwipeBack } from "../../hooks/useEdgeSwipeBack";
import NativePullToRefresh from "./NativePullToRefresh";
import { usePushNotifications } from "../Notifications/hooks/usePushNotifications";

/**
 * Native (Capacitor) app shell: same provider stack and top bar as the mobile
 * web layout (ProtectedLayout), with a bottom tab bar as primary navigation.
 * The drawer sidebar remains as the "More" surface for secondary
 * destinations. Safe-area insets keep the bars clear of the notch and home
 * indicator (env() resolves to 0 where there is none).
 */
const NativeTabLayout: React.FC = () => {
  const location = useLocation();
  const { trackFeature } = useAnalytics();
  // Left-edge right-swipe = back (iOS convention). The drawer deliberately
  // does NOT own this edge on native — it opens via the burger / More tab.
  useEdgeSwipeBack();
  // Push lives with the authenticated shell: registration, taps, and the
  // badge refresh on a foreground arrival. Mounted here and nowhere else.
  usePushNotifications();

  // Tap-to-paint transition timing: nav chrome marks the tap (lib/navPerf)
  // and the destination's first painted frame reports the duration, so
  // navigation regressions show up in PostHog rather than reviews.
  useEffect(() => {
    const mark = takeNavMark();
    if (!mark) return;
    let inner: number | undefined;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        trackFeature("perf_nav_transition", {
          source: mark.label,
          to_path: location.pathname,
          duration_ms: Math.round(performance.now() - mark.startedAt),
        });
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== undefined) cancelAnimationFrame(inner);
    };
  }, [location.pathname, location.search, trackFeature]);

  return (
    <TopBarSlotProvider>
      <Box sx={{ display: "flex", width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>
        <MobileTopBar />
        <Sidebar isMobileMode />
        <NativePullToRefresh />

        <Box
          component="main"
          sx={{
            marginTop: `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px))`,
            width: "100%",
            maxWidth: "100vw",
            minHeight: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px - env(safe-area-inset-top, 0px))`,
            // Content must never end flush behind the fixed tab bar
            paddingBottom: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + ${BOTTOM_TAB_BAR_SAFE_AREA})`,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
            overflowX: "hidden",
          }}
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>

        <BottomTabBar />
      </Box>
    </TopBarSlotProvider>
  );
};

export default NativeTabLayout;
